import { FC, useCallback } from 'react';
import PinPadDialog from 'components/PinPad/PinPadDialog';
import { useLanguage } from 'context/AppContext';
import { useLoginWithPin } from 'hooks/useLoginWithPin';
import { usePinLockout } from 'hooks/usePinLockout';
import { usePinOrgId } from 'hooks/usePinOrgId';
import { formatClockTime } from 'utils/helpers/time.helpers';

type Props = {
  open: boolean;
  email: string;
  onClose: VoidFunction;
  // continue with a normal password login for this user
  onUsePassword: VoidFunction;
};

// PIN login for one user; the link under the pad falls back to the password and leaves the PIN untouched
const PinLoginDialog: FC<Props> = ({ open, email, onClose, onUsePassword }) => {
  const lang = useLanguage()[0].PinLogin.login;
  const loginWithPin = useLoginWithPin();
  const [lockedUntil, refreshLockout] = usePinLockout(usePinOrgId(), email);

  const onSubmit = useCallback(
    async (pin: string): Promise<string | undefined> => {
      try {
        const result = await loginWithPin(email, pin);
        if (result.status === 'wrong_pin') return lang.wrongPin;
        if (result.status === 'locked_out') {
          refreshLockout();
          return lang.lockedOut({ seconds: result.retryAfterSeconds });
        }
        if (result.status === 'password_required') onUsePassword();
      } catch {
        return lang.error;
      }
    },
    [loginWithPin, email, lang, onUsePassword, refreshLockout],
  );

  return (
    <PinPadDialog
      open={open}
      title={lang.title}
      description={lang.description({ email })}
      onSubmit={onSubmit}
      onCancel={onClose}
      secondaryLabel={lang.usePassword}
      onSecondary={onUsePassword}
      disabledMessage={lockedUntil ? lang.lockedUntil({ time: formatClockTime(lockedUntil) }) : undefined}
    />
  );
};

export default PinLoginDialog;
